import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Routes } from 'router/routes';
import dataService from 'services/dataService';
import { showResult } from 'infrastructure/hooks/utils';
import { apiDueAppointments } from 'services/dataService/resources';

import { getLast7DaysRangeTimestamps } from '../utils/time-range';

import type {
  ICallDueItem,
  ICallsDueResponse,
  ICallDueAppointment,
} from 'components/molecules/oneplaform/overview-patients/types';

dayjs.extend(utc);
dayjs.extend(timezone);

const fetchCallsDue = async (
  startTime: number,
  endTime: number,
): Promise<ICallsDueResponse> => {
  const endpoint = apiDueAppointments({
    startTime,
    endTime,
  });

  const { data, error } = await dataService.getList<ICallsDueResponse>(
    endpoint,
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  if (!data) {
    const message = 'No data received from due appointments API';
    showResult(message);
    throw new Error(message);
  }

  if (!Array.isArray(data.data)) {
    const message = 'Invalid response structure from due appointments API';
    showResult(message);
    throw new Error(message);
  }

  if (typeof data.count !== 'number' || data.count < 0) {
    const message = 'Invalid count value from due appointments API';
    showResult(message);
    throw new Error(message);
  }

  return data;
};

const transformCallDueData = (
  appointment: ICallDueAppointment,
): ICallDueItem => {
  const localDateTime = dayjs
    .tz(appointment.scheduledDate, 'America/Chicago')
    .local();
  return {
    patientId: appointment.patientGuid,
    patientName: `${appointment.patientFirstName} ${appointment.patientLastName}`,
    dueDateTime: localDateTime.isValid()
      ? localDateTime.format('MM-DD-YYYY, [at] h:mm A')
      : 'Invalid Date',
  };
};

export const useCallsDue = () => {
  const navigate = useNavigate();
  const { from: startTime, to: endTime } = getLast7DaysRangeTimestamps();

  const { data, isLoading, error } = useQuery({
    queryKey: ['calls-due', startTime, endTime],
    queryFn: () => fetchCallsDue(startTime, endTime),
  });

  const handleItemClick = (patientId: string) => {
    navigate(Routes.patientGuid(patientId));
  };

  const callDueDetails = data?.data.map(transformCallDueData) || [];

  return {
    count: data?.count ?? 0,
    details: callDueDetails,
    onItemClick: handleItemClick,
    isLoading,
    error,
  };
};
